/**
 * Build the StudyUser store
 *
 * Follows best practices from Redux documentation:
 *   - Single source of truth
 *   - State/Store is read-only
 *   - Changes are made with pure functions
 *
 * See http://redux.js.org/docs/recipes/StructuringReducers.html for specific
 * docs on structuring reducers
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import studyUser actions
import * as Actions from './studyUserActions';

/**
 * studyUserList reducer -
 *
 * Accepts arbitrary list arguments and recursively builds nested list as needed
 *
 * NOTE: this is never called directly. Only by parent 'studyUser' reducer (defined
 * below) when dealing with a LIST action
 */
function studyUserList(state = {
  /**
   * The "items" object defines the default state for a list
   *
   * NOTE: This is for reference only. The list is not actually initialized here.
   * The actual init happens the first time REQUEST_LIST is called.
   */
  items: [] // array of _id's
  , isFetching: false
  , error: null
  , didInvalidate: false
  , lastUpdated: null
  , pagination: {}
  , filter: {}

}, action) {
  // console.log("DEBUG", state, action.listArgs);
  let nextAction = JSON.parse(JSON.stringify(action)); // Only change copy. NOT the  original object
  nextAction.listArgs.shift();

  /**
   * Check for nested list --
   * If the action is asking for a nested state, like lists[workout][123ABC],
   * then recursively return an _additional_ studyUserList reducer.
   *
   * Otherwise, return the actual studyUser lists' store
   */
  if(nextAction.listArgs.length > 0) {
    /**
     * The action is asking for a nested state, like lists[workout][123ABC].
     * Let's nest it by returning an additional studyUserList reducer and trying again.
     */
    return {
      ...state
      , [nextAction.listArgs[0]]: studyUserList(state[nextAction.listArgs[0]] || {}, nextAction)
    }
  } else {
    /**
     * Stop nesting. Instead listen for the actions and respond accordingly.
     */
    switch(action.type) {
      case Actions.INVALIDATE_STUDY_USER_LIST: {
        return {
          ...state
          , didInvalidate: true
        }
      }
      case Actions.REQUEST_STUDY_USER_LIST: {
        return {
          ...state
          , items: [] // array of _id's
          , isFetching: true
          , error: null
          , lastUpdated: null
          , pagination: state.pagination || {}
          , filter: state.filter || {}
        }
      }
      case Actions.RECEIVE_STUDY_USER_LIST: {
        if(!action.success) {
          return {
            ...state
            , items: [] // array of _id's
            , isFetching: false
            , error: action.error
            , lastUpdated: action.receivedAt
          }
        } else {
          let idArray = [];
          for(const item of action.list) {
            idArray.push(item._id);
          }
          return {
            ...state
            , items: idArray
            , isFetching: false
            , error: action.error || null
            , didInvalidate: false
            , lastUpdated: action.receivedAt
            , completionMap: action.completionMap ? action.completionMap : null 
          }
        }
      }
      case Actions.ADD_STUDY_USER_TO_LIST:
        let idArray = [...state.items];
        idArray.indexOf(action.id) === -1 ? idArray.push(action.id) : console.log("Item is already in list");
        return {
          ...state
          , items: idArray
          , isFetching: false
          , error: action.error || null
          , didInvalidate: false
          , lastUpdated: action.recievedAt
        }

      case Actions.REMOVE_STUDY_USER_FROM_LIST:
        idArray = [...state.items]
        let index = idArray.indexOf(action.id);
        if(index != -1) {
          idArray.splice(index, 1);
        } else {
          console.log("item not in list");
        }
        return {
          ...state
          , items: idArray
          , isFetching: false
          , error: action.error || null
          , didInvalidate: false
          , lastUpdated: action.receivedAt
        }

      case Actions.SET_STUDY_USER_FILTER: {
        return {
          ...state
          , filter: action.filter
        }
      }
      case Actions.SET_STUDY_USER_PAGINATION: {
        return {
          ...state
          , pagination: action.pagination
        }
      }
      default:
        return state;
    }
  }
}

/**
 * Primary studyUser reducer -
 *
 * This is the single source of truth for all things 'studyUser' related within the
 * application. The primary components of the reducer are defined in detail below.
 *
 * The basic idea is that the reducer listens for actions indicating a desired
 * state change and the reducer returns a new _copy_ of the state accordingly.
 */
function studyUser(state = {

  /**
   * "defaultItem" defines fields for a _new_ studyUser
   * any component that creates a new studyUser object should store a copy of this
   * in its state
   */
  defaultItem: {
    _createdBy: null
    , _lastUpdateBy: null
    , _organization: null
    , _group: null
    , _user: null
    , admin: false
    , email: ''
    // , expiration: ''
    , siteNumber: ''
    , status: 'active'
    , siteRole: ''
  }

  , availableRoles: [
    // site-level roles
    { display: 'SITE - Principal Investigator', val: 'site-principal-investigator' }
    , { display: 'SITE - Sub-investigator', val: 'site-sub-investigator' }
    , { display: 'SITE - Rater', val: 'site-rater' }
    , { display: 'SITE - Study Coordinator', val: 'site-study-coordinator' }
    , { display: 'SITE - Study Coordinator Unblinded', val: 'site-study-coordinator-unblinded' }
    , { display: 'SITE - Study Nurse', val: 'site-study-nurse' }
    , { display: 'SITE - Pharmacist', val: 'site-pharmacist' }
    , { display: 'SITE - Pharmacist Unblinded', val: 'site-pharmacist-unblinded' }
    , { display: 'SITE - Drug Delivery Contact', val: 'site-drug-delivery-contact' }
    , { display: 'SITE - Data Manager', val: 'site-data-manager' }
    , { display: 'SITE - Regulatory', val: 'site-regulatory' }
    , { display: 'SITE - Labs', val: 'site-labs' }
    , { display: 'SITE - Budgets', val: 'site-budget' }
    , { display: 'SITE - Contracts', val: 'site-contracts' }
    , { display: 'SITE - Operations-Management', val: 'site-operations-management' }
    , { display: 'SITE - Safety', val: 'site-safety' }
    , { display: 'SITE - Quality Control', val: 'site-quality-control' }
    , { display: 'SITE - Other', val: 'site-other' }

    // sponsor-level roles
    , { display: 'SPONSOR - Study Lead', val: 'sponsor-study-lead' }
    , { display: 'SPONSOR - Study Team', val: 'sponsor-study-team' }
    , { display: 'SPONSOR - Medical Monitor', val: 'sponsor-medical-monitor' }
    , { display: 'SPONSOR - Other', val: 'sponsor-other' }
    , { display: 'SPONSOR - Safety', val: 'sponsor-safety' }

    // CRO-level roles
    , { display: 'CRO - PM', val: 'cro-pm' }
    , { display: 'CRO - Study Team', val: 'cro-study-team' }
    , { display: 'CRO - CRA', val: 'cro-cra' }
    , { display: 'CRO - CRA Unblinded', val: 'cro-cra-unblinded' }
    , { display: 'CRO - Medical Monitor', val: 'cro-medical-monitor' }
    , { display: 'CRO - Other', val: 'cro-other' }
    , { display: 'CRO - Clinical Lead', val: 'cro-clinical-lead' }
    , { display: 'CRO - Safety', val: 'cro-safety' }
    , { display: 'CRO - Clinical Lead Unblinded', val: 'cro-clinical-lead-unblinded' }

    // miscellaneous roles (for analytics only)
    , { display: 'Student', val: 'student' }
    , { display: 'Vendor', val: 'vendor' }
    // , { display: 'MISC - Lab Kit Delivery', val: 'lab-kit-delivery' }
    , { display: 'Pro-ficiency', val: 'pro-ficiency' }
    , { display: 'Unknown', val: 'unknown' }
  ]

  , statuses: [
    'active'
    , 'expired'
    , 'deactivated'
  ]

  /**
   * "byId" is an object map of all studyUser items in the store. The map's keys are
   * the Mongo ids of the objects by default
   */
  , byId: {}

  /**
   * "selected" is a single _selected_ entity within the store
   *
   * For example, when changing the name of a studyUser, the single studyUser
   * being edited would be defined by "selected"
   */
  , selected: {
    id: null
    , isFetching: false
    , error: null
    , didInvalidate: false
    , lastUpdated: null
    , getItem: () => {
      return null
    }
  }

  , util: {
    getList: (...listArgs) => {
      return null
    }
    , getListInfo: (...listArgs) => {
      return null
    }
    , getKeyArrayFromList: (...listArgs) => {
      return null
    }
  }

  /**
   * "lists" corresponds to individual instances of the studyUserList reducer as
   * defined above.
   *
   * NOTE: when requesting a list, if args are undefined, the lists defaults to
   * lists['all']
   */
  , lists: {}

}, action) {
  /**
   * Listen for the actions and respond accordingly.
   */
  let nextState;
  switch(action.type) {
    /**
     * SINGLE STUDY_USER ACTIONS
     */
    case Actions.REQUEST_SINGLE_STUDY_USER: {
      nextState = {
        ...state
        , selected: {
          id: action.id
          , isFetching: true
          , error: null
        }
      }
      break;
    }
    case Actions.RECEIVE_SINGLE_STUDY_USER: {
      if(action.success) {
        nextState = {
          ...state
          , byId: {
            ...state.byId
            , [action.id]: action.item
          }
          , selected: {
            id: action.id
            , isFetching: false
            , error: null
            , didInvalidate: false
            , lastUpdated: action.receivedAt
          }
        }
      } else {
        nextState = {
          ...state
          , selected: {
            isFetching: false
            , error: action.error
            , lastUpdated: action.receivedAt
          }
        }
      }
      break;
    }
    case Actions.ADD_SINGLE_STUDY_USER_TO_MAP: {
      // add this studyUser to the map
      nextState = {
        ...state
        , byId: {
          ...state.byId
          , [action.id]: action.item
        }
      }
      break;
    }
    case Actions.REQUEST_CREATE_STUDY_USER: {
      nextState = {
        ...state
        , selected: {
          id: null
          , isFetching: true
          , error: null
        }
      }
      break;
    }
    case Actions.RECEIVE_CREATE_STUDY_USER: {
      if(action.success) {
        nextState = {
          ...state
          , byId: {
            ...state.byId
            , [action.id]: action.item
          }
          , selected: {
            id: action.id
            , isFetching: false
            , error: null
            , didInvalidate: false
            , lastUpdated: action.receivedAt
          }
        }
      } else {
        nextState = {
          ...state
          , selected: {
            isFetching: false
            , error: action.error
            , lastUpdated: action.receivedAt
          }
        }
      }
      break;
    }
    case Actions.REQUEST_UPDATE_STUDY_USER: {
      nextState = {
        ...state
        , selected: {
          id: action.id
          , isFetching: true
          , error: null
        }
      }
      break;
    }
    case Actions.RECEIVE_UPDATE_STUDY_USER: {
      if(action.success) {
        nextState = {
          ...state
          , byId: {
            ...state.byId
            , [action.id]: action.item
          }
          , selected: {
            id: action.id
            , isFetching: false
            , error: null
            , didInvalidate: false
            , lastUpdated: action.receivedAt
          }
        }
      } else {
        nextState = {
          ...state
          , selected: {
            isFetching: false
            , error: action.error
            , lastUpdated: action.receivedAt
          }
        }
      }
      break;
    }
    case Actions.REQUEST_DELETE_STUDY_USER: {
      nextState = {
        ...state
        , selected: {
          id: action.id
          , isFetching: true
          , error: null
        }
      }
      break;
    }
    case Actions.RECEIVE_DELETE_STUDY_USER: {
      if(action.success) {
        // remove this object from map
        let newIdMap = { ...state.byId };
        delete newIdMap[action.id]; //remove key
        nextState = {
          ...state
          , byId: newIdMap
          , selected: {
            id: null
            , isFetching: false
            , error: null
            , didInvalidate: false
            , lastUpdated: action.receivedAt
          }
        }
      } else {
        nextState = {
          ...state
          , selected: {
            isFetching: false
            , error: action.error
            , lastUpdated: action.receivedAt
          }
        }
      }
      break;
    }
    case Actions.INVALIDATE_SELECTED_STUDY_USER: {
      nextState = {
        ...state
        , selected: {
          didInvalidate: true
        }
      }
      break;
    }

    /**
     * LIST ACTIONS
     */
    case Actions.INVALIDATE_STUDY_USER_LIST:
    case Actions.REQUEST_STUDY_USER_LIST:
    case Actions.SET_STUDY_USER_FILTER:
    case Actions.ADD_STUDY_USER_TO_LIST:
    case Actions.REMOVE_STUDY_USER_FROM_LIST:
    case Actions.SET_STUDY_USER_PAGINATION: {
      nextState = {
        ...state
        , lists: {
          ...state.lists
          , [action.listArgs[0]]: studyUserList(state.lists[action.listArgs[0]] || {}, action)
        }
      }
      break;
    }
    case Actions.RECEIVE_STUDY_USER_LIST: {
      // add items to "byId" before we forward to individual list reducer
      let newIdMap = { ...state.byId };
      if(action.success) {
        for(const item of action.list) {
          newIdMap[item._id] = item;
        }
      }
      nextState = {
        ...state
        , byId: newIdMap
        , lists: {
          ...state.lists
          , [action.listArgs[0]]: studyUserList(state.lists[action.listArgs[0]], action)
        }
      }
      break;
    }
    default: {
      nextState = state
      break;
    }
  }

  //set getter method for returning single selected item
  nextState.selected = {
    ...nextState.selected
    , getItem: () => {
      if(!nextState.selected.id || nextState.selected.didInvalidate) {
        return null
      } else {
        return nextState.byId[nextState.selected.id]
      }
    }
  }
  nextState.util.getList = (...listArgs) => {
    /**
     * utility method for a) determining if a list exists and b) getting those list objects
     * this can be used in the render function of a component to avoid having to
     * type: lists.player && lists.player.[id] && lists.player.[id].items
     * if list doesnt exist yet, it returns null, else returns array of objects
     * not meant to replace the map and individual list reducers, but to reduce
     * boiler plate and produce cleaner code in the front end components.
     */
    if(listArgs.length === 0) {
      // If no arguments passed, make the list we want "all"
      listArgs = ["all"];
    }
    // // so the new api works without changing the listArgs in all the components
    // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
    let nextList = nextState.lists;
    for(var i = 0; i < listArgs.length; i++) {
      if(nextList[listArgs[i]]) {
        nextList = nextList[listArgs[i]];
      } else {
        nextList = null;
        break;
      }
    }
    if(!nextList || !nextList.items || nextList.didInvalidate) {
      return null
    } else {
      return nextList.items.map((item) => nextState.byId[item])
    }
  }
  nextState.util.getListInfo = (...listArgs) => {
    /**
     * utility method for determining the fetching status of
     * any list. If the list exists it returns the whole list object.
     * Otherwise it returns null.
     * Useful on the front end to do something like this:
     * const studyUserList = studyUserStore.util.getListInfo(...studyUserListArgs);
     * const isFetching = studyUserList ? studyUserList.isFetching
     * const studyUserListError = studyUserList ? studyUserList.error
     * Instead of something like this:
     * const isFetching = (
     *  studyUserStore 
     *  && studyUserStore.lists
     *  && studyUserStore.lists[1stListArg]
     *  && studyUserStore.lists[1stListArg][2ndListArg]
     *  && studyUserStore.lists[1stListArg][2ndListArg].isFetching
     * )
     */
    if(listArgs.length === 0) {
      // If no arguments passed, make the list we want "all"
      listArgs = ["all"];
    }
    // // so the new api works without changing the listArgs in all the components
    // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
    let nextList = nextState.lists;
    for(var i = 0; i < listArgs.length; i++) {
      if(nextList[listArgs[i]]) {
        nextList = nextList[listArgs[i]];
      } else {
        nextList = null;
        break;
      }
    }
    if(nextList) {
      return nextList
    } else {
      return null
    }
  }
  nextState.util.getKeyArrayFromList = (key, ...listArgs) => {
    /**
     * utility method for returning an ARRAY of all of the "key" values
     * for the objects defined in a certain list. for example, if we have
     * a list defined by listArgs ("status", "published"), we can return an
     * array of all that list's author ids by calling:
     * Reducer.getKeyArrayFromList("_author","status","published")
     */
    if(listArgs.length === 0) {
      // If no arguments passed, make the list we want "all"
      listArgs = ["all"];
    }
    // // so the new api works without changing the listArgs in all the components
    // listArgs = listArgs.map(arg => arg.toString().replaceAll('study', 'group'));
    let nextList = nextState.lists;
    for(var i = 0; i < listArgs.length; i++) {
      if(nextList[listArgs[i]]) {
        nextList = nextList[listArgs[i]];
      } else {
        nextList = null;
        break;
      }
    }
    if(!nextList || !nextList.items || nextList.didInvalidate) {
      return null
    } else {
      return nextList.items.map((item) => nextState.byId[item][key])
    }
  }
  return nextState;
}

export default studyUser;



// WEBPACK FOOTER //
// modules/studyUser/studyUserReducers.js